/*
 * Code generated by Speakeasy (https://speakeasy.com). DO NOT EDIT.
 */

import {
  InvalidateQueryFilters,
  QueryClient,
  useQuery,
  UseQueryResult,
  useSuspenseQuery,
  UseSuspenseQueryResult,
} from "@tanstack/react-query";
import { GramError } from "../models/errors/gramerror.js";
import {
  ConnectionError,
  InvalidRequestError,
  RequestAbortedError,
  RequestTimeoutError,
  UnexpectedClientError,
} from "../models/errors/httpclienterrors.js";
import { ResponseValidationError } from "../models/errors/responsevalidationerror.js";
import { SDKValidationError } from "../models/errors/sdkvalidationerror.js";
import { ServiceError } from "../models/errors/serviceerror.js";
import {
  GetMcpServerUsageImpactRequest,
  GetMcpServerUsageImpactSecurity,
} from "../models/operations/getmcpserverusageimpact.js";
import { useGramContext } from "./_context.js";
import {
  QueryHookOptions,
  SuspenseQueryHookOptions,
  TupleToPrefixes,
} from "./_types.js";
import {
  buildMcpServerUsageImpactQuery,
  McpServerUsageImpactQueryData,
  prefetchMcpServerUsageImpact,
  queryKeyMcpServerUsageImpact,
} from "./mcpServerUsageImpact.core.js";
export {
  buildMcpServerUsageImpactQuery,
  type McpServerUsageImpactQueryData,
  prefetchMcpServerUsageImpact,
  queryKeyMcpServerUsageImpact,
};

export type McpServerUsageImpactQueryError =
  | ServiceError
  | GramError
  | ResponseValidationError
  | ConnectionError
  | RequestAbortedError
  | RequestTimeoutError
  | InvalidRequestError
  | UnexpectedClientError
  | SDKValidationError;

/**
 * getMcpServerUsageImpact mcpServers
 *
 * @remarks
 * Preview who a breaking change to an MCP server affects: the server's active signed-in connections and when each subject last used them. Authorizes the caller as updateMcpServer does. Only sessions issued by the server's user session issuer are counted, so servers without sign-in report sign_in_required=false and no subjects.
 */
export function useMcpServerUsageImpact(
  request: GetMcpServerUsageImpactRequest,
  security?: GetMcpServerUsageImpactSecurity | undefined,
  options?: QueryHookOptions<
    McpServerUsageImpactQueryData,
    McpServerUsageImpactQueryError
  >,
): UseQueryResult<
  McpServerUsageImpactQueryData,
  McpServerUsageImpactQueryError
> {
  const client = useGramContext();
  return useQuery({
    ...buildMcpServerUsageImpactQuery(
      client,
      request,
      security,
      options,
    ),
    ...options,
  });
}

/**
 * getMcpServerUsageImpact mcpServers
 *
 * @remarks
 * Preview who a breaking change to an MCP server affects: the server's active signed-in connections and when each subject last used them. Authorizes the caller as updateMcpServer does. Only sessions issued by the server's user session issuer are counted, so servers without sign-in report sign_in_required=false and no subjects.
 */
export function useMcpServerUsageImpactSuspense(
  request: GetMcpServerUsageImpactRequest,
  security?: GetMcpServerUsageImpactSecurity | undefined,
  options?: SuspenseQueryHookOptions<
    McpServerUsageImpactQueryData,
    McpServerUsageImpactQueryError
  >,
): UseSuspenseQueryResult<
  McpServerUsageImpactQueryData,
  McpServerUsageImpactQueryError
> {
  const client = useGramContext();
  return useSuspenseQuery({
    ...buildMcpServerUsageImpactQuery(
      client,
      request,
      security,
      options,
    ),
    ...options,
  });
}

export function setMcpServerUsageImpactData(
  client: QueryClient,
  queryKeyBase: [
    parameters: {
      id: string;
      gramSession?: string | undefined;
      gramKey?: string | undefined;
      gramProject?: string | undefined;
    },
  ],
  data: McpServerUsageImpactQueryData,
): McpServerUsageImpactQueryData | undefined {
  const key = queryKeyMcpServerUsageImpact(...queryKeyBase);

  return client.setQueryData<McpServerUsageImpactQueryData>(key, data);
}

export function invalidateMcpServerUsageImpact(
  client: QueryClient,
  queryKeyBase: TupleToPrefixes<
    [parameters: {
      id: string;
      gramSession?: string | undefined;
      gramKey?: string | undefined;
      gramProject?: string | undefined;
    }]
  >,
  filters?: Omit<InvalidateQueryFilters, "queryKey" | "predicate" | "exact">,
): Promise<void> {
  return client.invalidateQueries({
    ...filters,
    queryKey: ["@gram/client", "mcpServers", "getUsageImpact", ...queryKeyBase],
  });
}

export function invalidateAllMcpServerUsageImpact(
  client: QueryClient,
  filters?: Omit<InvalidateQueryFilters, "queryKey" | "predicate" | "exact">,
): Promise<void> {
  return client.invalidateQueries({
    ...filters,
    queryKey: ["@gram/client", "mcpServers", "getUsageImpact"],
  });
}
